import React, { FC, useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import {
  IdpHttpClientApi,
  SamlMeta,
  UpdateSamlPayload,
} from 'api/security/idp';
import { Button } from 'components/atoms/button';
import { Form } from 'components/atoms/form';
import { confirm } from 'components/atoms/modal';
import { toastService } from 'components/molecules/toast';
import {
  ButtonGroup,
  Drawer,
  DrawerContent,
} from 'components/organisms/task-pane/task-detail';
import { useTranslate, useTranslation } from 'hooks/use-translation';
import {
  useGetSamlById,
  useQuerySamlMeta,
  useSamlCreation,
  useSamlUpdate,
} from 'queries/saml';
import { getChangedPayload, SetupFormField } from '../utils';
import { SamlForm, SamlFormPage } from './saml-form';

const DrawerFooter = styled(ButtonGroup)`
  display: flex;
  flex: 1;
`;

interface SamlMetaForm extends SamlMeta {
  downloadUrl: string;
}

export const SetupSamlDrawer: FC<{
  visible: boolean;
  onClose: () => void;
  idpId?: string;
  page?: SamlFormPage;
}> = ({ visible, onClose: closeMe, idpId, page }) => {
  const modalTitle = useTranslation('settings.security.saml.drawer.title');
  const { data: oldSamlConfig } = useGetSamlById(idpId);
  const [savedIdp, setSavedIpd] = useState(idpId);
  const { mutate: createSaml, isLoading: isSaving } = useSamlCreation();
  const { mutate: updateSaml, isLoading: isUpdating } = useSamlUpdate();
  const [formPage, setFormPage] = useState(page || SamlFormPage.SETUP);

  const translate = useTranslate();
  const [form] =
    Form.useForm<{ setupPage: SetupFormField; metaPage: SamlMetaForm }>();

  const { isLoading: isMetaLoading, refetch: fetchSamlMeta } = useQuerySamlMeta(
    savedIdp,
    (samlMeta) => {
      form.setFieldsValue({ metaPage: samlMeta });
    },
  );

  useEffect(() => {
    form.setFieldsValue({
      setupPage: {
        audienceUrl: oldSamlConfig?.idpMeta?.audienceUrl,
        ssoUrl: oldSamlConfig?.idpMeta?.ssoUrl,
        certificate: oldSamlConfig?.idpMeta?.certificate,
      },
    });
  }, [oldSamlConfig, form]);

  const [isFormInvalid, setIsFormInvalid] = useState(true);

  useEffect(() => {
    if (!visible) {
      return;
    }
    setIsFormInvalid(true);
    setSavedIpd(idpId);
    setFormPage(SamlFormPage.SETUP);
  }, [idpId, visible]);

  const showMetaForm = useCallback(
    (samlIdp: string) => {
      fetchSamlMeta();
      IdpHttpClientApi.getSamlMetaDownload(samlIdp).then((downloadUrl) => {
        setFormPage(SamlFormPage.META);
        form.setFieldsValue({ metaPage: { downloadUrl } });
      });
    },
    [fetchSamlMeta, form],
  );

  useEffect(() => {
    if (!idpId || page !== SamlFormPage.META || !visible) {
      return;
    }
    setFormPage(SamlFormPage.META);
    showMetaForm(idpId);
  }, [idpId, page, showMetaForm, visible]);
  const enableSaml = () => {
    if (!savedIdp) {
      return;
    }

    updateSaml(
      { idpId: savedIdp, enabled: true },
      {
        onSuccess: closeMe,
      },
    );
  };
  const onSubmit = () => {
    const upsertSaml = () => {
      if (oldSamlConfig?.idpId) {
        const payload = getChangedPayload(
          oldSamlConfig,
          form.getFieldsValue().setupPage,
        );

        return updateSaml(payload as UpdateSamlPayload, {
          onSuccess: () => {
            setSavedIpd(oldSamlConfig?.idpId);
            showMetaForm(oldSamlConfig?.idpId);
            toastService({
              title: translate('config.saved'),
              message: translate('setup_profile.sso.config.saved'),
            }).success();
          },
        });
      }

      return createSaml(
        {
          saml: {
            ...form.getFieldsValue().setupPage,
          },
        },
        {
          onSuccess: (newSamlConfig) => {
            setSavedIpd(newSamlConfig.idpId);
            showMetaForm(newSamlConfig.idpId);
            toastService({
              title: translate('config.saved'),
              message: translate('setup_profile.sso.config.saved'),
            }).success();
          },
        },
      );
    };

    if (formPage === SamlFormPage.SETUP) {
      upsertSaml();
    } else if (!oldSamlConfig?.enabled) {
      confirm({
        title: translate('settings.security.saml.model.title'),
        content: translate('settings.security.saml.model.body'),
        okText: translate('settings.security.saml.model.ok'),
        cancelText: translate('settings.security.saml.model.cancel'),
        onOk: enableSaml,
        closable: true,
      });
    } else {
      closeMe();
    }
  };

  const onValueChange = (_, allValues) => {
    const values = Object.values(allValues);
    const isInvalid =
      values.includes(undefined) ||
      values.includes(null) ||
      values.includes('');

    setIsFormInvalid(isInvalid);
  };

  return (
    <Drawer
      placement="right"
      visible={visible}
      width={360}
      onClose={closeMe}
      footer={
        <DrawerFooter>
          <Button
            onClick={formPage === SamlFormPage.SETUP ? closeMe : onSubmit}
          >
            <FormattedMessage
              id={page === SamlFormPage.SETUP ? 'cancel' : 'close'}
            />
          </Button>
          {formPage === SamlFormPage.SETUP && (
            <Button
              iconPlacement="right"
              loading={isSaving || isUpdating || isMetaLoading}
              disabled={isFormInvalid}
              type="primary"
              onClick={onSubmit}
            >
              <FormattedMessage id="next" />
            </Button>
          )}
        </DrawerFooter>
      }
    >
      <DrawerContent>
        <DrawerContent.Title>{modalTitle}</DrawerContent.Title>
        <DrawerContent.Content>
          <SamlForm
            form={form}
            onValueChange={onValueChange}
            formPage={formPage}
          />
        </DrawerContent.Content>
      </DrawerContent>
    </Drawer>
  );
};
